import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type FormEvent } from "react";
import {
  PANEL_OPERATOR_DEPARTURE_REASONS,
  PANEL_PAUSE_REASONS,
  panelMembershipInputSchema,
  type BulkImportResult,
  type PanelOperatorDepartureReason,
  type PanelPauseReason,
} from "@titleverify/shared";
import { apiErrorText, apiFetch, type PanelMembership } from "../api.js";
import { useAuth } from "../auth.js";
import { formatDate } from "./caseDisplay.js";
import { AdminBreadcrumb } from "./AdminHome.js";

/**
 * THE PANEL — which verified attorneys are in the pool for which registry.
 *
 * ===========================================================================
 * READ THIS BEFORE ADDING A COLUMN, A SORT OR A WORD OF COPY.
 * ===========================================================================
 *
 * This screen is the operator's door onto the pool the mechanical assignment
 * computes over (CLAUDE.md §2A — a non-technical operator must be able to manage
 * every reference dataset without a migration or a redeploy). Before it existed,
 * seating a member needed raw SQL, which meant the rotation had no pool and every
 * assignment failed.
 *
 * IT IS AN INTERNAL OPERATIONS TOOL AND IT IS NOT A DIRECTORY. COMPLIANCE
 * Constraint 1 (Supreme Court Rules 1988, Rule 39) applies inside the admin panel
 * exactly as it does at the client edge, so three things are absent by design and
 * must stay absent:
 *
 *  1. NO RANK, SCORE, STAR, BADGE OR QUALITY SIGNAL of any kind, and no sort that
 *     produces one. The list is ordered by registry, then by how long a member has
 *     held their seat — deliberately NOT by who is owed work next, which is the
 *     obvious thing to sort by and would read as a league table to every human who
 *     has ever seen one.
 *  2. NO WORKLOAD NUMBER. `lastAssignedAt` is a date the server derived from the
 *     assignment rows; nothing counts cases, computes a share or measures a
 *     backlog, so nothing here may imply one (CLAUDE.md non-negotiable 1).
 *  3. NO FEE, PRICE, RATE OR PAYOUT. D1 is open (non-negotiable 3).
 *
 * Nothing on this screen is reachable by a client. The server refuses
 * `panel:manage` to every role but admin regardless of what this page renders.
 *
 * TODO-BLOCKED-Q1E — the panel model presumes ATTORNEY_BRIEF Q1(e) is answered
 * yes. It is unanswered.
 */
export function AdminPanel() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  // Which row's action drawer is open. One at a time — two half-finished changes
  // to who is in the pool is how the wrong one gets saved.
  const [acting, setActing] = useState<string | null>(null);

  const { data, error, isPending } = useQuery({
    queryKey: ["admin", "panel-memberships"],
    queryFn: async () =>
      apiFetch<{ panelMemberships: PanelMembership[] }>("/admin/panel-memberships", {
        token: await getToken(),
      }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "panel-memberships"] });

  if (isPending) return <p className="muted" role="status">Loading the panel…</p>;
  if (error) return <p role="alert">{apiErrorText(error, "Could not load the panel.")}</p>;

  const seats = data.panelMemberships;

  return (
    <section className="wide">
      <AdminBreadcrumb />
      <header className="page-head">
        <p className="eyebrow">Who can be assigned where</p>
        <h2>Panel</h2>
        {/*
          "NOBODY AT THE PLATFORM PICKS THE ATTORNEY" WAS AN ABSOLUTE, AND THE
          OVERRIDE DOOR FALSIFIES IT. CaseDetail's own override copy says the
          opposite in as many words — "You name the attorney and the rule does
          not" — so one of the two screens was lying to the same operator, and
          the one making the flattering claim was this one. The mechanism is:
          rotation is the default and takes no attorney argument at all; the
          override is a declared exception that stores a reason and who the rule
          would have chosen. Both sentences now describe that.
        */}
        <p className="lede">
          The pool each case is assigned from, per registry. Cases go by rotation; overriding
          it is a separate, recorded act. An empty panel means those cases wait.
        </p>
      </header>

      <SeatForm getToken={getToken} onDone={invalidate} />
      <ImportPanel getToken={getToken} onDone={invalidate} />

      {seats.length === 0 ? (
        <p className="empty">
          No seats yet. Until a registry has one, cases routed to it stay routed — they are
          not sent elsewhere.
        </p>
      ) : (
        <>
          {/*
            THIS CAVEAT USED TO SAY the screen "cannot show who is next", while a
            cell below told any never-assigned member they "go first". The screen
            contradicted itself, and the caveat was the wrong end: where one member
            is plainly least-recently-assigned, this screen DOES show who is next.

            It was also the more damaging error, because it sold concealment as a
            safeguard. Concealment is not the safeguard and was never meant to be —
            PANEL_ROTATION_RULE reason 3 is that a third party can RECOMPUTE the
            order from `assignments`, `cases.registry_district` and
            `panel_memberships` alone, without trusting us. A rule an excluded
            attorney can check is a rule an operator can also read ahead. That is
            the trade, taken deliberately.

            So what stops an operator timing a seating or a pause to land a case on
            a chosen attorney is NOT that they cannot predict the order. It is that
            every input is recorded with an actor: seats carry sourced_by, pauses
            and departures carry who and why, and since 0027 each assignment
            carries the pre-filter census plus the seat's age and who seated it.
            The manoeuvre is visible, not impossible — and the copy must not
            promise the stronger thing.
          */}
          <p className="caveat">
            Never assigned here goes first, then least-recently-assigned, then longest-held
            seat, then a per-case digest. So you can often tell who is next. That is
            deliberate — anyone can recompute the order from the record — but it means
            seating or pausing near an assignment is worth explaining.
          </p>

          <div className="table-wrap" tabIndex={0} role="region" aria-label="Panel seats">
            <table>
              <thead>
                <tr>
                  <th>Registry</th>
                  <th>Enrolment</th>
                  <th>Standing</th>
                  <th>Last assigned here</th>
                  {/*
                    A DATE COLUMN HOLDS A DATE. This header read "Seated on" over
                    `seat.source` — the operator's own provenance prose — so the
                    cell rendered "invited 2026-07, accepted by email" under a
                    heading promising a date. Free text presented as a timestamp is
                    a fact the platform never stated (CLAUDE.md non-negotiable 1),
                    and `joinedAt` — the real seating date, and the rotation's
                    second-order tiebreak — was fetched and thrown away.

                    Provenance is not lost, it is moved: `source` and the admin who
                    seated them are in the row's drawer, labelled as what they are.
                    Prose of arbitrary length does not belong in a table cell on a
                    375px screen anyway.
                  */}
                  <th>Seated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {seats.map((seat) => (
                  <Fragment key={seat.id}>
                    <tr>
                      <td>
                        <code>{seat.registryOfficeCode}</code>
                      </td>
                      <td>
                        {/*
                          The identifier, not a name — the API holds none. It is
                          also the honest one to lead with: the enrolment number is
                          what an admin verified against a source.
                        */}
                        {seat.enrolmentNo ?? <span className="muted">none recorded</span>}
                      </td>
                      <td>
                        <Standing seat={seat} />
                      </td>
                      <td>
                        <LastAssigned at={seat.lastAssignedAt} />
                      </td>
                      <td>{formatDate(seat.joinedAt)}</td>
                      <td>
                        {/*
                          THE DRAWER OPENS ON AN ENDED SEAT TOO, and that is the
                          rest of the "Seated on" fix. An ended seat had no control
                          at all, so its provenance had nowhere to be read — and an
                          ended seat is exactly the row someone reconstructing a
                          past assignment needs to look at. It opens with the
                          record and no actions, because there is nothing left to
                          act on.
                        */}
                        <button
                          type="button"
                          className="quiet compact"
                          aria-expanded={acting === seat.id}
                          aria-controls={`act-${seat.id}`}
                          onClick={() => setActing((c) => (c === seat.id ? null : seat.id))}
                        >
                          {acting === seat.id ? "Close" : seat.leftAt ? "Details" : "Change"}
                        </button>
                      </td>
                    </tr>
                    {acting === seat.id ? (
                      <tr>
                        <td colSpan={6} id={`act-${seat.id}`}>
                          <div className="stack">
                            <SeatProvenance seat={seat} />
                            {seat.leftAt ? null : (
                              <SeatActions
                                seat={seat}
                                getToken={getToken}
                                onDone={() => {
                                  invalidate();
                                  setActing(null);
                                }}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <p className="muted">
            Seats are identified by the enrolment number that was checked, because that is the
            only identifier the API returns. Ended seats stay listed — the pool as it stood is
            what shows a past assignment ran over the right set.
          </p>
        </>
      )}
    </section>
  );
}

type TokenFn = { getToken: () => Promise<string | null> };

/**
 * Available / paused / ended.
 *
 * Neutral in all three directions on purpose. A member being paused or having
 * left is a fact about the routing pool, not good or bad news about them, so it
 * gets no colour with an opinion in it — the same choice AdminRegistry makes for
 * an inactive office.
 */
function Standing({ seat }: { seat: PanelMembership }) {
  if (seat.leftAt) {
    return (
      <>
        <span className="status status-neutral">ended</span>
        <br />
        <small>
          {/*
            The two codes the DATABASE writes are spelt out here because they
            are not in DEPARTURE_COPY — that map is the operator's vocabulary,
            and no operator may choose either of these. Falling through to
            "Reason not recorded" would say the reason was missing when it is
            sitting in the row.
          */}
          {seat.leftReason === "VERIFICATION_ENDED"
            ? "Ended by their verification being withdrawn."
            : seat.leftReason === "ACCOUNT_ERASED"
              ? "Ended by their account being erased."
              : DEPARTURE_COPY[seat.leftReason as PanelOperatorDepartureReason] ??
                "Reason not recorded."}
        </small>
      </>
    );
  }
  if (seat.pausedAt) {
    return (
      <>
        <span className="status status-neutral">paused</span>
        <br />
        <small>
          {PAUSE_COPY[seat.pausedReason as PanelPauseReason] ?? "Reason not recorded."} No new
          cases while paused.
        </small>
      </>
    );
  }
  return (
    <>
      <span className="status status-neutral">available</span>
      <br />
      <small>In the pool for this registry.</small>
    </>
  );
}

/**
 * NULL IS "NEVER", NOT A BLANK. A blank cell in a date column reads as missing
 * data; never-assigned is a fact, and it is the fact that sorts this member ahead
 * of anyone already assigned here — `last_assigned_at ASC NULLS FIRST`
 * (CLAUDE.md non-negotiable 1).
 *
 * SAID "so they go first" until it was noticed that two never-assigned members
 * both claimed it. True of the group, false of the individual: among them the
 * order is longest-held seat, then a per-case digest. The caveat above the table
 * carries the full rule, so this cell states only its own row's fact.
 */
function LastAssigned({ at }: { at: string | null }) {
  if (at === null) {
    return (
      <>
        <strong>Never here</strong>
        <br />
        <small>No case at this registry yet, so they go ahead of anyone assigned.</small>
      </>
    );
  }
  return <>{new Date(at).toLocaleDateString()}</>;
}

/**
 * WHERE THE SEAT CAME FROM — who put this member in the pool, when, and on what
 * basis.
 *
 * The payload has carried all three since the panel routes were written, and the
 * comment on `present()` (apps/api/src/panel/routes.ts) says provenance is sent
 * "so the operator sees who seated each member and on what basis". Nothing
 * rendered `sourcedBy` or `sourcedAt`, and `source` was mislabelled as a date, so
 * that sentence was false about the only screen it describes. This is what makes
 * it true.
 *
 * `sourcedBy` IS A RAW USER ID and is shown as one, because the API holds no name
 * for an admin either. An id an operator can match on the access log beats a
 * blank, and inventing a display name for a person the server never described
 * would be the fabrication this whole feature is guarding against.
 */
function SeatProvenance({ seat }: { seat: PanelMembership }) {
  return (
    <dl>
      <dt>Seated</dt>
      <dd>{formatDate(seat.joinedAt)}</dd>

      <dt>Basis</dt>
      <dd>{seat.source}</dd>

      <dt>Recorded by</dt>
      <dd>
        <code>{seat.sourcedBy}</code>, {formatDate(seat.sourcedAt)}
      </dd>

      {seat.leftAt ? (
        <>
          <dt>Ended</dt>
          <dd>{formatDate(seat.leftAt)}</dd>
        </>
      ) : null}
    </dl>
  );
}

/**
 * Seating a member. The form validates against `panelMembershipInputSchema` — the
 * exact schema the server parses — so a bad row is caught here first and refused
 * there regardless (§2A: the same validation at every door).
 *
 * The two ids are typed rather than picked from a list, and that is deliberate
 * rather than unfinished: a searchable roster of professionals to pick from is
 * one edit away from being a directory, and the operator gets the user id from
 * the roster screen, which already exists for exactly this.
 */
function SeatForm({ getToken, onDone }: TokenFn & { onDone: () => void }) {
  const [form, setForm] = useState({ registryOfficeCode: "", professionalUserId: "", source: "" });
  const [clientError, setClientError] = useState<string | null>(null);

  const seat = useMutation({
    mutationFn: async (body: unknown) =>
      apiFetch<PanelMembership>("/admin/panel-memberships", {
        token: await getToken(),
        method: "POST",
        body: body as Record<string, unknown>,
      }),
    onSuccess: () => {
      setForm({ registryOfficeCode: "", professionalUserId: "", source: "" });
      onDone();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    setClientError(null);
    const parsed = panelMembershipInputSchema.safeParse(form);
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setClientError(issue ? `${issue.path.join(".")}: ${issue.message}` : "invalid");
      return;
    }
    seat.mutate(parsed.data);
  }

  return (
    <form onSubmit={submit}>
      <fieldset>
        <legend>Seat a member</legend>
        {/*
          THE THIRD WAY TO STEER, AND THE CHEAPEST TO MAKE VISIBLE.
          ===================================================================
          Pausing and ending both state their consequence before the click. This
          form did not, and it has one: a member who has never been assigned at
          this registry sorts FIRST (rankPanelCandidates ranks nulls ahead of
          every date), so seating someone immediately before assigning puts them
          ahead of every member who has been assigned here. Every row still reads
          `panel_rotation`.

          "HANDS THEM THE NEXT CASE" IS WHAT THIS USED TO SAY, AND IT WAS FALSE
          whenever another never-assigned member already held a seat: nulls tie,
          and the tie breaks on the longer-standing seat, so the newcomer goes
          SECOND. The identical sentence had already been corrected a hundred
          lines below (`LastAssigned` says "ahead of anyone assigned"), which is
          the tell — one copy of a claim gets fixed and its twin ships on.

          It is not a defect in the ordering — never-assigned genuinely has waited
          longest, and any other rule would need a stored position for someone to
          tamper with. It is a defect in an operator not being told, which is what
          COMPLIANCE Constraint 1 turns into a real risk here: a decision that
          steers work should never be one an honest person makes by accident.
        */}
        <div className="consequence">
          <p>
            A member who has never been assigned here goes{" "}
            <strong>ahead of everyone who has</strong> — and behind any never-assigned
            member already seated.
          </p>
        </div>
        <div className="field">
          <label htmlFor="panel-office">registry office code *</label>
          <input
            type="text"
            id="panel-office"
            value={form.registryOfficeCode}
            onChange={(e) => setForm((f) => ({ ...f, registryOfficeCode: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="panel-user">professional user id *</label>
          <input
            type="text"
            id="panel-user"
            value={form.professionalUserId}
            onChange={(e) => setForm((f) => ({ ...f, professionalUserId: e.target.value }))}
          />
          <small className="field-hint">
            From the roster. Only a verified attorney-at-law can be seated — the server and the
            database both refuse anyone else.
          </small>
        </div>
        <div className="field">
          <label htmlFor="panel-source">source *</label>
          <input
            type="text"
            id="panel-source"
            value={form.source}
            onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
          />
          <small className="field-hint">
            What the seating was based on — &ldquo;invited 2026-07, accepted by email&rdquo;.
            Saving records you as the person who seated them, and when.
          </small>
        </div>

        {clientError ? <p role="alert">{clientError}</p> : null}
        {seat.error ? <p role="alert">{apiErrorText(seat.error, "Could not seat them.")}</p> : null}

        <div className="actions">
          <button type="submit" disabled={seat.isPending}>
            {seat.isPending ? "Seating…" : "Seat member"}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

/**
 * Pausing and ending, on one drawer because they are the two things that can
 * happen to a live seat — and separated within it because they are not the same
 * act. Pausing is reversible and keeps the member's place in the rotation; ending
 * is one-way.
 *
 * Both state their consequence BEFORE the confirm (`.consequence`), because both
 * change who receives work and neither is obvious from its verb.
 */
function SeatActions({
  seat,
  getToken,
  onDone,
}: TokenFn & { seat: PanelMembership; onDone: () => void }) {
  const [pauseReason, setPauseReason] = useState<PanelPauseReason>("UNAVAILABLE_TEMPORARILY");
  const [endReason, setEndReason] = useState<PanelOperatorDepartureReason>("AT_PROFESSIONALS_REQUEST");

  const availability = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      apiFetch<PanelMembership>(`/admin/panel-memberships/${seat.id}/availability`, {
        token: await getToken(),
        method: "POST",
        body,
      }),
    onSuccess: onDone,
  });

  const end = useMutation({
    mutationFn: async () =>
      apiFetch<PanelMembership>(`/admin/panel-memberships/${seat.id}/end`, {
        token: await getToken(),
        method: "POST",
        body: { reason: endReason },
      }),
    onSuccess: onDone,
  });

  return (
    <div className="stack">
      {seat.pausedAt ? (
        <div className="consequence">
          <p>
            <strong>Resuming</strong> puts them back in the pool for this registry from the next
            case. Their place in the rotation was kept while they were paused.
          </p>
          {availability.error ? (
            <p role="alert">{apiErrorText(availability.error, "Could not resume them.")}</p>
          ) : null}
          <div className="actions">
            <button
              type="button"
              disabled={availability.isPending}
              onClick={() => availability.mutate({ paused: false })}
            >
              {availability.isPending ? "Resuming…" : "Resume"}
            </button>
          </div>
        </div>
      ) : (
        <div className="consequence">
          {/*
            THE WARNING THAT MATTERS ON THIS SCREEN. Pausing is the one lever here
            that can hand-pick an attorney without looking like it: pause everyone
            but one and the rotation, untouched and honest-looking, has exactly one
            candidate. Every pause is recorded with who did it and why, and this
            says so before the click rather than after.

            THE SECOND SENTENCE IS NEW AND IS A FACT, NOT A DETERRENT. Until the
            panel census the pause and the assignment were two records joined only
            by their timestamps; a case assigned from a panel paused down to one
            recorded a candidate set of one and looked exactly like a case from a
            genuine one-member panel. Assignments now carry the seats that were
            excluded and why, so the operator is told what the record will say.
            Understating a safeguard that exists is as wrong as claiming one that
            does not — so this is stated, and stated in one line.
          */}
          <p>
            <strong>Pausing</strong> stops new cases reaching them here. Their place is kept, so
            resuming costs them nothing.
          </p>
          {/*
            THIS NAMED PAUSING AND NOTHING ELSE, which told an operator exactly
            which lever is watched and, by omission, which is not. Ending a seat
            thins the pool identically and was the better-covered manoeuvre:
            until 0029 the census filtered on `left_at IS NULL`, so an ended seat
            did not appear as an exclusion — it did not appear at all, and the
            case's audit entry then asserted a clean, smaller panel. Both levers
            are now counted and both are named here. Understating a safeguard is
            as wrong as claiming one that does not exist; so is listing half the
            doors.
          */}
          <p>
            Pausing or ending everyone but one member leaves the rotation with one candidate.
            Both record you and the reason, and every case assigned afterwards records which
            seats were excluded and why.
          </p>
          <div className="field">
            <label htmlFor={`pause-${seat.id}`}>Why?</label>
            <select
              id={`pause-${seat.id}`}
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value as PanelPauseReason)}
            >
              {PANEL_PAUSE_REASONS.map((code) => (
                <option key={code} value={code}>
                  {PAUSE_COPY[code]}
                </option>
              ))}
            </select>
          </div>
          {availability.error ? (
            <p role="alert">{apiErrorText(availability.error, "Could not pause them.")}</p>
          ) : null}
          <div className="actions">
            <button
              type="button"
              disabled={availability.isPending}
              onClick={() => availability.mutate({ paused: true, reason: pauseReason })}
            >
              {availability.isPending ? "Pausing…" : "Pause"}
            </button>
          </div>
        </div>
      )}

      {/*
        SAID "their place in the rotation starts again". It does not, and the
        difference is the whole point of the control.

        Reproduced against the live database: a member was assigned a case, ended
        their seat, and rejoined on a brand-new row. Their rotation key came back
        byte-identical, because it is not on the seat. `readPanelCensus` derives
        `last_assigned_at` from `assignments` joined to `cases.registry_district`,
        keyed on the PERSON and the registry — `panel_memberships` has no such
        column to reset. Only `joined_at`, the second tiebreak, is new.

        PRESERVING IT IS THE SAFE DIRECTION, so this corrects the copy and not the
        mechanism. If a rejoin did reset the key, the member would come back with
        NULL — and NULL sorts FIRST. End-and-reseat would then be a two-click way
        to move a chosen attorney to the head of the queue, with each half looking
        like ordinary administration. The rule surviving the seat is what makes
        that manoeuvre pointless.
      */}
      <div className="consequence">
        <p>
          <strong>Ending</strong> the seat is one-way — rejoining is a new seat. It does not
          reset their turn: that is counted from when they were last assigned here, and it
          outlives the seat. It says nothing about their enrolment or their conduct.
        </p>
        <div className="field">
          <label htmlFor={`end-${seat.id}`}>Why?</label>
          <select
            id={`end-${seat.id}`}
            value={endReason}
            onChange={(e) => setEndReason(e.target.value as PanelOperatorDepartureReason)}
          >
            {PANEL_OPERATOR_DEPARTURE_REASONS.map((code) => (
              <option key={code} value={code}>
                {DEPARTURE_COPY[code]}
              </option>
            ))}
          </select>
          <small className="field-hint">{DEPARTURE_CAVEAT[endReason]}</small>
        </div>
        {end.error ? <p role="alert">{apiErrorText(end.error, "Could not end this seat.")}</p> : null}
        <div className="actions">
          <button type="button" className="danger" disabled={end.isPending} onClick={() => end.mutate()}>
            {end.isPending ? "Ending…" : "End this seat"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportPanel({ getToken, onDone }: TokenFn & { onDone: () => void }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const importer = useMutation({
    mutationFn: async (rows: unknown[]) =>
      apiFetch<BulkImportResult>("/admin/panel-memberships/import", {
        token: await getToken(),
        method: "POST",
        body: { rows },
      }),
    onSuccess: (r) => {
      setResult(r);
      onDone();
    },
    // Refreshed on failure too: a part-completed import leaves seats behind, and
    // the operator has to be able to see which.
    onError: onDone,
  });

  function run() {
    setParseError(null);
    setResult(null);
    let rows: unknown;
    try {
      rows = JSON.parse(text);
    } catch {
      setParseError("Paste a JSON array of rows.");
      return;
    }
    if (!Array.isArray(rows)) {
      setParseError("The import must be a JSON array.");
      return;
    }
    importer.mutate(rows);
  }

  return (
    <details>
      <summary>Bulk import (JSON array)</summary>
      <div className="field">
        <label htmlFor="panel-import-rows" className="visually-hidden">
          Bulk import (JSON array)
        </label>
        <textarea
          id="panel-import-rows"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder='[{ "registryOfficeCode": "COL-01", "professionalUserId": "…", "source": "invited 2026-07" }]'
        />
      </div>
      {parseError ? <p role="alert">{parseError}</p> : null}
      {/*
        The request FAILING, as distinct from importing with failed rows. Rows go
        in one at a time on the server, precisely so one bad row cannot roll back a
        good one — so a request that stopped partway may well have written some,
        and this browser was never told how many. Claiming either number would be
        inventing the one fact we did not receive.
      */}
      {importer.error ? (
        <p role="alert">
          {apiErrorText(importer.error, "The import did not finish.")} Rows go in one at a time,
          so some may already have been written — this page was not told which. The table below
          has been reloaded and is what the panel now holds.
        </p>
      ) : null}
      <div className="actions">
        <button type="button" onClick={run} disabled={importer.isPending}>
          {importer.isPending ? "Importing…" : "Import"}
        </button>
      </div>

      {/*
        Honest per-row outcome. Neutral `.outcome`, not an alert: the good rows
        landed, and dressing the whole thing in danger colours would report them
        as a failure too.
      */}
      {result ? (
        <div className="outcome">
          <p>
            Imported {result.inserted}. Failed {result.failed.length}.
          </p>
          {result.failed.length > 0 ? (
            <ul>
              {result.failed.map((f) => (
                <li key={f.index}>
                  row {f.index}: {f.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </details>
  );
}

/**
 * The reason codes in an operator's words, keyed off the shared constants so a
 * code added there without copy here fails to compile rather than rendering a
 * bare SCREAMING_SNAKE token to someone who then guesses what it means.
 */
const PAUSE_COPY: Record<PanelPauseReason, string> = {
  AT_PROFESSIONALS_REQUEST: "They asked to stop receiving new cases for now",
  UNAVAILABLE_TEMPORARILY: "Leave, illness or capacity",
  OTHER: "Something else",
};

const DEPARTURE_COPY: Record<PanelOperatorDepartureReason, string> = {
  AT_PROFESSIONALS_REQUEST: "They asked to come off this panel",
  WITHDRAWN_BY_PLATFORM: "This platform has decided to stop sending them work here",
  OTHER: "Something else",
};

/**
 * The caveat under each departure code. `WITHDRAWN_BY_PLATFORM` carries the same
 * warning as its twin in the verification vocabulary, and for the same reason:
 * it is the platform's own commercial choice, and any surface that let it read as
 * a sanction would be asserting a conduct finding only the Bar Association of Sri
 * Lanka and the Supreme Court may make.
 */
const DEPARTURE_CAVEAT: Record<PanelOperatorDepartureReason, string> = {
  AT_PROFESSIONALS_REQUEST: "They asked. Nothing is being alleged and nothing is being decided.",
  WITHDRAWN_BY_PLATFORM:
    "This platform's own decision to stop sending them cases from this registry. It asserts nothing about their enrolment and nothing about their conduct.",
  OTHER:
    "Prefer a specific code where one is true — a year from now nobody will be able to reconstruct this one.",
};
